import {
  LabwareItemInput,
  LabwareTypeInput,
  PlannerOptionsInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { createAction } from '@reduxjs/toolkit';

import {
  LATEST_WORKFLOW_MIGRATION_VERSION,
  LoadedWorkflowLatest,
} from './chain';
import { _deriveWorkflowData } from './deriveWorkflowData';
import { _migrateLoadedVisualBuilderData } from './migrateLoadedVisualBuilderData';

export { type LoadedWorkflowLatest, LATEST_WORKFLOW_MIGRATION_VERSION };

/** data derived from the API. For Visual Builder, it's read-only from the Workflow Config, never saved into `layout.data`. */
export type DerivedWorkflowData = {
  published: boolean;
};

export type WorkflowMetadata = {
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  validBatchData: {
    reason: string;
    validBatchNumbers: number[];
  };
  syncedPlanId: string | null;
};

export type LoadedWorkflowWithDerived = {
  derived: DerivedWorkflowData;
  //TODO: remove this when the migration is complete
  visualBuilderSaved: LoadedWorkflowLatest & {
    labware_types: LabwareTypeInput[];
    labware: LabwareItemInput[];
  };
  metadata: WorkflowMetadata;
  plannerOptions: PlannerOptionsInput;
  schedulerVersion: string;
};

const normalizePlanningTargetsForVisualBuilder = (
  planningTargets: unknown
):
  | {
      data: {
        consistency?: Record<string, unknown[]>;
        timing_targets?: unknown[];
      };
    }
  | undefined => {
  if (planningTargets == null || typeof planningTargets !== 'object') {
    return undefined;
  }

  const source = (planningTargets as any).data ?? planningTargets;
  const rawConsistency = source?.consistency;

  let consistency: Record<string, unknown[]> | undefined;
  if (Array.isArray(rawConsistency)) {
    consistency = { default: rawConsistency };
  } else if (rawConsistency != null && typeof rawConsistency === 'object') {
    consistency = rawConsistency as Record<string, unknown[]>;
  }

  const timingTargets = Array.isArray(source?.timing_targets)
    ? source.timing_targets
    : undefined;

  if (consistency == null && timingTargets == null) {
    return undefined;
  }

  return {
    data: {
      consistency,
      timing_targets: timingTargets,
    },
  };
};

const normalizeTransportConfigForVisualBuilder = (
  transportConfig: unknown
):
  | {
      id?: string;
      type?: 'V1' | 'V3';
      version?: number;
    }
  | undefined => {
  if (transportConfig == null || typeof transportConfig !== 'object') {
    return undefined;
  }

  const source = transportConfig as Record<string, unknown>;
  const id =
    typeof source.id === 'string'
      ? source.id
      : typeof source.transport_config_id === 'string'
      ? source.transport_config_id
      : undefined;

  const typeSource = source.type ?? source.transport_config_type;
  const type =
    typeSource === 'V1' || typeSource === 'V3' ? typeSource : undefined;

  const version =
    typeof source.version === 'number' ? source.version : undefined;

  if (id == null && type == null && version == null) {
    return undefined;
  }

  return { id, type, version };
};

export const loadExistingWorkflow = createAction<LoadedWorkflowWithDerived>(
  'workflow/loadExistingWorkflow'
);

/** Run the migration on saved data, and include the derived data from the Workflow Config. */
export const transformLoadedWorkflow = (
  workflowConfig: any
): LoadedWorkflowWithDerived => {
  const migrated = _migrateLoadedVisualBuilderData(workflowConfig);
  const planningTargets = normalizePlanningTargetsForVisualBuilder(
    workflowConfig?.workflow?.planning_targets
  );
  const transportConfig = normalizeTransportConfigForVisualBuilder(
    workflowConfig?.workcell?.transport_config
  );

  return {
    derived: _deriveWorkflowData(workflowConfig),
    metadata: {
      createdBy: workflowConfig.metadata.creator_username ?? 'Unknown',
      createdAt: workflowConfig.metadata.created_at ?? new Date().toISOString(),
      updatedAt: workflowConfig.metadata.updated_at ?? new Date().toISOString(),
      publishedAt: workflowConfig.metadata.published_at ?? null,
      validBatchData: {
        reason: workflowConfig.metadata.valid_batch_data?.reason ?? '',
        validBatchNumbers:
          workflowConfig.metadata.valid_batch_data?.valid_batch_numbers ?? [],
      },
      syncedPlanId: workflowConfig.metadata.synced_plan_id,
    },
    visualBuilderSaved: {
      ...migrated,
      workcellTransportConfigId:
        migrated.workcellTransportConfigId ??
        transportConfig?.id ??
        workflowConfig.workcell?.workcell_id ??
        undefined,
      workcellTransportConfigType:
        migrated.workcellTransportConfigType ??
        transportConfig?.type ??
        undefined,
      workcellTransportConfigVersion:
        migrated.workcellTransportConfigVersion ??
        transportConfig?.version ??
        undefined,
      labware_types: workflowConfig.workflow.labware_types,
      labware: workflowConfig.workflow.labware,
      ...(planningTargets !== undefined
        ? { planning_targets: planningTargets }
        : {}),
      visualWorkflow: {
        ...migrated.visualWorkflow,
        workflowName:
          workflowConfig.name ?? migrated.visualWorkflow.workflowName,
      },
    },
    plannerOptions: (workflowConfig.options?.planner ??
      {}) as PlannerOptionsInput,
    schedulerVersion: workflowConfig.scheduler_config.version,
  };
};
